// M4-1 / R8 — **THE PHONE'S DRAWER RAIL TOGGLE, AND THE TWO-LISTENER LAW THAT KEEPS IT WORKING.**
//
// Extracted out of `src/App.tsx` by M1 (2026-08-17), which is R8's ledger paid in the currency it
// prefers: **the thing that had to move is the thing nothing could reach.** The mousedown/click
// reasoning below was traced from a real defect — every drawer on a phone was dead, because closing
// on MOUSEDOWN removed the tab before mouseup and the browser never dispatched a click at all — and
// while it sat inside an 11,400-line file no instrument in this repo could read it, let alone RUN it.
//
// It holds hooks and moved anyway, which is the class `TierCards`, `MicButton` and
// `AttachmentTray` already moved as: the reach this buys is the IMPORT, not the render.
import { useEffect, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";

export function MobileDrawerToggle() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.dataset.rail = open ? "open" : "closed";
    return () => { delete document.body.dataset.rail; };
  }, [open]);

  // Tapping outside slides it away. So does picking a drawer: a tab click IS a selection, and the
  // rail has done its job once the panel is open.
  //
  // M4-1 (2026-07-25) — WHY THIS IS TWO LISTENERS AND NOT ONE. Closing on MOUSEDOWN killed every drawer
  // on a phone: the star opened the rail, and tapping Journal just closed the whole rail without opening
  // anything. Traced, not guessed. The closed-rail CSS sets `visibility: hidden` and
  // `pointer-events: none` and DELIBERATELY does not transition them (they were transitioned once and
  // the rail intermittently refused to appear), so the instant `data-rail` flips to "closed" the tab is
  // gone. Closing on mousedown therefore removed the tab BEFORE mouseup, the browser never dispatched a
  // click at all, and the tab's own handler never ran. The rail's close handler was eating the tap.
  //
  // So: a tap on a TAB is left completely alone on mousedown, and the rail closes on the CLICK that
  // follows — by which time the tab's own handler has already opened its drawer. A tap anywhere ELSE
  // still closes on mousedown, which is what keeps dismissing the rail feeling immediate.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".rail-toggle")) return;
      if (el.closest(".drawer-tab")) return; // let the tab's own click through — see above
      setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest(".rail-toggle")) return;
      if (el.closest(".drawer-tab")) setOpen(false); // the drawer is open now; the rail is done
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("click", onClick);
    };
  }, [open]);

  return (
    <button
      className={open ? "rail-toggle is-open" : "rail-toggle"}
      type="button"
      aria-expanded={open}
      aria-label={open ? "Hide the drawers" : "Show the drawers"}
      onClick={() => setOpen((v) => !v)}
    >
      {open ? <ArrowRight size={18} /> : <Sparkles size={18} />}
    </button>
  );
}

